import { Minus, TrendingUp, Trophy } from 'lucide-react';
import { Stage, type Team } from 'modules/client/tournaments/domain/types';
import getFormIcon from 'modules/client/tournaments/domain/utils/get-form-icon';
import React from 'react';

interface StageGroupsSectionProps {
  stage: Stage;
  teams: Team[];
}

const StageGroupsSection: React.FC<StageGroupsSectionProps> = ({ stage, teams }) => {
  const getQualificationStatus = (position: number) => {
    if (position <= 2) {
      return {
        color: 'bg-emerald-50 border-l-4 border-emerald-500',
        text: 'text-emerald-700',
        icon: <TrendingUp className="text-emerald-600" size={14} />,
        status: 'Плей-офф',
      };
    }
    return {
      color: 'bg-gray-50 border-l-4 border-gray-300',
      text: 'text-gray-600',
      icon: <Minus className="text-gray-400" size={14} />,
      status: 'Исключён',
    };
  };

  const getTeamInfo = (teamId: number) => {
    return teams.find((t) => t.id === teamId);
  };

  if (!stage.groups || stage.groups.length === 0) {
    return (
      <div className="flex-center flex-1 text-balance py-8 text-center">
        <p className="text-gray-500">{'Групповая система не\u00A0используется в этом этапе'}</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="mb-8 text-center">
        <h1 className="mb-2 text-3xl font-bold text-gray-900">{stage.name}</h1>
        <p className="text-gray-600">{stage.description}</p>
      </div>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 xl:grid-cols-3">
        {stage.groups.map((group) => {
          const groupTeams = stage.teams.filter((team) => group.teams.includes(team.id));
          const sortedTeams = [...groupTeams].sort((a, b) => {
            return a.position - b.position;
          });

          return (
            <div key={group.id} className="overflow-hidden rounded-lg bg-white shadow-md">
              <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 px-6 py-4 text-white">
                <h2 className="flex items-center space-x-2 text-xl font-bold">
                  <Trophy size={20} />
                  <span>{group.name}</span>
                </h2>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">#</th>
                      <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">Команда</th>
                      <th className="px-3 py-2 text-center text-xs font-medium uppercase text-gray-500">И</th>
                      <th className="px-3 py-2 text-center text-xs font-medium uppercase text-gray-500">О</th>
                      <th className="px-3 py-2 text-center text-xs font-medium uppercase text-gray-500">РМ</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {sortedTeams.map((stageTeam, index) => {
                      const teamInfo = getTeamInfo(stageTeam.teamId);
                      const qualification = getQualificationStatus(index + 1);
                      if (!teamInfo) {
                        return null;
                      }

                      return (
                        <tr key={stageTeam.id} className={`transition-colors hover:bg-gray-50 ${qualification.color}`}>
                          <td className="px-3 py-3">
                            <div className="flex items-center space-x-2">
                              <span className={`text-sm font-bold ${qualification.text}`}>{index + 1}</span>
                              {qualification.icon}
                            </div>
                          </td>
                          <td className="px-3 py-3">
                            <div className="flex items-center space-x-2">
                              <span className="text-lg">
                                <img src={teamInfo.logo} alt="" className="size-8 rounded-lg object-contain" />
                              </span>
                              <div>
                                <div className="text-sm font-medium text-gray-900">{teamInfo.shortName}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-3 text-center text-sm text-gray-900">{stageTeam.played}</td>
                          <td className="px-3 py-3 text-center text-sm font-bold text-gray-900">{stageTeam.points}</td>
                          <td className="px-3 py-3 text-center">
                            <span
                              className={`text-sm font-medium ${
                                stageTeam.goalDifference > 0
                                  ? 'text-emerald-600'
                                  : stageTeam.goalDifference < 0
                                    ? 'text-red-600'
                                    : 'text-gray-900'
                              }`}
                            >
                              {stageTeam.goalDifference > 0 ? '+' : ''}
                              {stageTeam.goalDifference}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="bg-gray-50 px-4 py-3">
                <div className="space-y-1 text-xs text-gray-600">
                  <div className="flex items-center space-x-2">
                    <div className="h-3 w-3 rounded bg-emerald-500"></div>
                    <span>Проходят в плей-офф</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Detailed Group Tables */}
      <div className="space-y-8">
        {stage.groups.map((group) => {
          const groupTeams = stage.teams.filter((team) => group.teams.includes(team.id));
          const sortedTeams = [...groupTeams].sort((a, b) => {
            return b.position - a.position;
          });

          return (
            <div key={`detailed-${group.id}`} className="overflow-hidden rounded-lg bg-white shadow-md">
              <div className="bg-emerald-600 px-6 py-4 text-white">
                <h3 className="text-lg font-bold">{group.name} - Подробная таблица</h3>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="min-w-[95px] px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">#</th>
                      <th className="min-w-[243px] px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Команда</th>
                      <th className="px-4 py-3 text-center text-xs font-medium uppercase text-gray-500">И</th>
                      <th className="px-4 py-3 text-center text-xs font-medium uppercase text-gray-500">В</th>
                      <th className="px-4 py-3 text-center text-xs font-medium uppercase text-gray-500">Н</th>
                      <th className="px-4 py-3 text-center text-xs font-medium uppercase text-gray-500">П</th>
                      <th className="px-4 py-3 text-center text-xs font-medium uppercase text-gray-500">ЗМ</th>
                      <th className="px-4 py-3 text-center text-xs font-medium uppercase text-gray-500">ПМ</th>
                      <th className="px-4 py-3 text-center text-xs font-medium uppercase text-gray-500">РМ</th>
                      <th className="px-4 py-3 text-center text-xs font-medium uppercase text-gray-500">О</th>
                      <th className="min-w-[168px] px-4 py-3 text-center text-xs font-medium uppercase text-gray-500">Форма</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {sortedTeams.map((stageTeam, index) => {
                      const teamInfo = getTeamInfo(stageTeam.teamId);
                      const qualification = getQualificationStatus(index + 1);
                      if (!teamInfo) {
                        return null;
                      }

                      return (
                        <tr key={stageTeam.id} className={`transition-colors hover:bg-gray-50 ${qualification.color}`}>
                          <td className="px-4 py-4">
                            <div className="flex items-center space-x-2">
                              <span className={`text-sm font-bold ${qualification.text}`}>{index + 1}</span>
                              {qualification.icon}
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex items-center space-x-3">
                              <span className="text-xl">
                                <img src={teamInfo.logo} alt="" className="size-10 rounded-lg object-contain" />
                              </span>
                              <div>
                                <div className="text-sm font-medium text-gray-900">{teamInfo.name}</div>
                                <div className="text-xs text-gray-500">{teamInfo.shortName}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4 text-center text-sm text-gray-900">{stageTeam.played}</td>
                          <td className="px-4 py-4 text-center text-sm text-gray-900">{stageTeam.won}</td>
                          <td className="px-4 py-4 text-center text-sm text-gray-900">{stageTeam.drawn}</td>
                          <td className="px-4 py-4 text-center text-sm text-gray-900">{stageTeam.lost}</td>
                          <td className="px-4 py-4 text-center text-sm text-gray-900">{stageTeam.goalsFor}</td>
                          <td className="px-4 py-4 text-center text-sm text-gray-900">{stageTeam.goalsAgainst}</td>
                          <td
                            className={`px-4 py-4 text-center text-sm font-medium ${
                              stageTeam.goalDifference > 0
                                ? 'text-emerald-600'
                                : stageTeam.goalDifference < 0
                                  ? 'text-red-600'
                                  : 'text-gray-900'
                            }`}
                          >
                            {stageTeam.goalDifference > 0 ? '+' : ''}
                            {stageTeam.goalDifference}
                          </td>
                          <td className="px-4 py-4 text-center text-sm font-bold text-gray-900">{stageTeam.points}</td>
                          <td className="px-4 py-4">
                            <div className="flex justify-center space-x-1">
                              {stageTeam.form.map((result, idx) => (
                                <div key={idx}>{getFormIcon(result)}</div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StageGroupsSection;
